/**
 * MODULE: platform/browser/frontend/hooks/useBrowserSessionPoll.ts
 * PURPOSE: Poll session status for live URL updates; detect server-side session expiry.
 * DEPENDENCIES: browserPageUrl, api config
 * PUBLIC_API: useBrowserSessionPoll
 * SIDE_EFFECTS: GET /platform/browser/sessions/{id} every 5s
 * AGENT_SAFETY: SAFE
 * RECENT: 2026-06-17 [2.2.1438] — CI timer types (`number` + `window.setInterval`) for Frontend Validation tsc on GHA.
 * RECENT_PRIOR: 2026-06-17 [2.2.1437] — Poll passes liveViewFallbackUrl for screencast mode.
 * RECENT_PRIOR: 2026-06-17 [2.2.1412] — Faster poll (2s) while agent SSE stream is active.
 */

import { useEffect, useRef } from 'react';
import { apiV1Url, getAuthHeaders } from '@shared/lib/frontend/api/config';
import { isMeaningfulBrowserUrl } from '../utils/browserPageUrl';

const POLL_MS = 5000;
const FAST_POLL_MS = 2000;
const CONNECT_POLL_MS = 800;

export function useBrowserSessionPoll(
  sessionId: string | null,
  onUrl: (url: string) => void,
  enabled: boolean,
  onSessionEnded?: () => void,
    onSessionMeta?: (meta: {
    timeoutMinutes?: number;
    liveViewUrl?: string;
    liveViewFullscreenUrl?: string;
    liveViewFallbackUrl?: string;
    actionCount?: number;
    recentActions?: Array<{ eventType: string; message: string; timestamp: string }>;
  }) => void,
  fastPoll = false,
  awaitingLiveView = false,
) {
  const onUrlRef = useRef(onUrl);
  const onSessionEndedRef = useRef(onSessionEnded);
  const onSessionMetaRef = useRef(onSessionMeta);
  onUrlRef.current = onUrl;
  onSessionEndedRef.current = onSessionEnded;
  onSessionMetaRef.current = onSessionMeta;

  useEffect(() => {
    if (!sessionId || !enabled) return;

    let cancelled = false;
    let timer: number | null = null;

    const stopPolling = () => {
      if (timer != null) {
        window.clearInterval(timer);
        timer = null;
      }
    };

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(
          apiV1Url(`/platform/browser/sessions/${encodeURIComponent(sessionId)}`),
          { headers: getAuthHeaders(), credentials: 'include' },
        );
        if (cancelled) return;

        if (res.status === 404) {
          stopPolling();
          onSessionEndedRef.current?.();
          return;
        }

        // 500 = transient (Browserbase debug blip) — keep polling; do not tear down session.
        if (res.status === 500) {
          return;
        }

        if (!res.ok) return;

        const data = await res.json();
        const status = data?.session?.status;
        if (status && status !== 'active') {
          stopPolling();
          onSessionEndedRef.current?.();
          return;
        }

        const url = data?.session?.currentUrl;
        if (typeof url === 'string' && isMeaningfulBrowserUrl(url)) {
          onUrlRef.current(url.trim());
        }

        const liveViewUrl = data?.session?.liveViewUrl;
        const liveViewFullscreenUrl = data?.session?.liveViewFullscreenUrl;
        const liveViewFallbackUrl = data?.session?.liveViewFallbackUrl;
        const timeoutMinutes = data?.session?.timeoutMinutes;
        const actionCount = data?.session?.actionCount;
        const recentActions = data?.session?.recentActions;
        const meta: {
          timeoutMinutes?: number;
          liveViewUrl?: string;
          liveViewFullscreenUrl?: string;
          liveViewFallbackUrl?: string;
          actionCount?: number;
          recentActions?: Array<{ eventType: string; message: string; timestamp: string }>;
        } = {};
        if (typeof liveViewUrl === 'string' && liveViewUrl.trim()) {
          meta.liveViewUrl = liveViewUrl.trim();
        }
        if (typeof liveViewFullscreenUrl === 'string' && liveViewFullscreenUrl.trim()) {
          meta.liveViewFullscreenUrl = liveViewFullscreenUrl.trim();
        }
        if (typeof liveViewFallbackUrl === 'string' && liveViewFallbackUrl.trim()) {
          meta.liveViewFallbackUrl = liveViewFallbackUrl.trim();
        }
        if (typeof timeoutMinutes === 'number' && timeoutMinutes > 0) {
          meta.timeoutMinutes = timeoutMinutes;
        }
        if (typeof actionCount === 'number' && actionCount >= 0) {
          meta.actionCount = actionCount;
        }
        if (Array.isArray(recentActions) && recentActions.length > 0) {
          meta.recentActions = recentActions;
        }
        if (
          meta.liveViewUrl ||
          meta.liveViewFullscreenUrl ||
          meta.liveViewFallbackUrl ||
          meta.timeoutMinutes ||
          meta.actionCount != null ||
          meta.recentActions
        ) {
          onSessionMetaRef.current?.(meta);
        }
      } catch {
        // ignore transient poll errors
      }
    };

    poll();
    const pollMs = awaitingLiveView ? CONNECT_POLL_MS : fastPoll ? FAST_POLL_MS : POLL_MS;
    timer = window.setInterval(poll, pollMs);
    return () => {
      cancelled = true;
      stopPolling();
    };
  }, [sessionId, enabled, fastPoll, awaitingLiveView]);
}
